import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import toast, { Toaster } from "react-hot-toast";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

import TransactionFilters from "../../components/admin/transactions/TransactionFilters";
import TransactionMessagesModal from "../../components/admin/transactions/TransactionMessagesModal";
import TransactionActionsBar from "../../components/admin/transactions-too/TransactionActionBar";
import TransactionListCards from "../../components/admin/transactions-too/TransactionListCard";

function getApiBase() {
  const env = (process.env.REACT_APP_API_URL || "").trim();
  if (env) return env.replace(/\/+$/, "");
  return window.location.origin.replace(/\/+$/, "");
}

const API_BASE = getApiBase();

// -------- helpers (no confirm(), no window.confirm)
function ConfirmModal({
  open,
  title,
  description,
  confirmText,
  cancelText,
  loading,
  onCancel,
  onConfirm,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
        {description && (
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
            {description}
          </p>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-900 text-sm disabled:opacity-50
                       dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
          >
            {cancelText || "Retour"}
          </button>

          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-3 py-2 rounded-md bg-amber-700 hover:bg-amber-800 text-white text-sm disabled:opacity-50"
          >
            {loading ? "Traitement..." : confirmText || "Confirmer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TransactionsPage({ currentAdmin }) {
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [destinationCountry, setDestinationCountry] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  // Socket guard
  const socketRef = useRef(null);

  // Selection + email modal
  const [selected, setSelected] = useState([]); // string IDs
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalTxId, setEmailModalTxId] = useState(null);
  const [bulkEmailMode, setBulkEmailMode] = useState(false);

  // Cancel confirm modals
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  const [confirmBulkCancel, setConfirmBulkCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Helpers

  const dedupeById = useCallback((list) => {
    const map = new Map();
    for (const tx of list) map.set(String(tx.id), tx);
    return Array.from(map.values());
  }, []);

  const upsertOne = useCallback((prev, updatedTxn) => {
    const id = updatedTxn?.id;
    if (id === undefined || id === null) return prev;

    const sid = String(id);
    const map = new Map(prev.map((t) => [String(t.id), t]));
    map.set(sid, { ...(map.get(sid) || {}), ...updatedTxn });
    return Array.from(map.values());
  }, []);

  const toggleSelect = (id) => {
    const sid = String(id);
    setSelected((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid],
    );
  };

  const toggleSelectAll = (filteredList) => {
    if (selected.length === filteredList.length) setSelected([]);
    else setSelected(filteredList.map((tx) => String(tx.id)));
  };

  const handleStatusChange = (transactionId, payload) => {
    const tid = String(transactionId);
    const patch =
      payload && typeof payload === "object" ? payload : { status: payload };

    setTransactions((prev) =>
      prev.map((tx) => (String(tx.id) === tid ? { ...tx, ...patch } : tx)),
    );
  };

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        search: search || "",
        status: statusFilter !== "All" ? statusFilter : "",
        type: typeFilter !== "All" ? typeFilter : "",
        fromDate: fromDate || "",
        toDate: toDate || "",
        destinationCountry: destinationCountry || "",
      });

      const res = await fetchWithAuth(
        `${API_BASE}/api/admin/transactions?${params.toString()}`,
      );

      if (!res.ok && res.status !== 304) {
        const json = await res.json().catch(() => null);
        const text = await res.text().catch(() => "");
        throw new Error(
          json?.message || text || "Erreur lors du chargement des transactions",
        );
      }

      const data = await res.json();
      setTransactions(dedupeById(data.transactions || []));
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    search,
    statusFilter,
    typeFilter,
    fromDate,
    toDate,
    destinationCountry,
    dedupeById,
  ]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    if (!currentAdmin) return;
    if (socketRef.current) return;

    const adminId =
      currentAdmin?.id != null ? String(currentAdmin.id) : "admin";

    const s = io(API_BASE, {
      auth: { userId: adminId },
      transports: ["websocket", "polling"], // more robust in shared hosting
    });

    socketRef.current = s;

    const onTxnUpdated = (updatedTxn) => {
      if (!updatedTxn || updatedTxn.id == null) return;
      setTransactions((prev) => dedupeById(upsertOne(prev, updatedTxn)));
    };

    s.off("transactionUpdated");
    s.on("transactionUpdated", onTxnUpdated);

    return () => {
      s.off("transactionUpdated", onTxnUpdated);
      s.disconnect();
      socketRef.current = null;
    };
  }, [currentAdmin, dedupeById, upsertOne]);

  // Filtered

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();

    return transactions.filter((tx) => {
      const paymentRef = String(tx.paymentRef || "").toLowerCase();
      const idStr = String(tx.id || "").toLowerCase();
      const email = String(tx.user?.email || "").toLowerCase();

      const matchesSearch =
        !s || paymentRef.includes(s) || idStr.includes(s) || email.includes(s);
      const matchesStatus =
        statusFilter === "All" || tx.status === statusFilter;
      const matchesType = typeFilter === "All" || tx.type === typeFilter;

      const matchesDestination =
        !destinationCountry ||
        String(tx.destinationCountry || "")
          .toLowerCase()
          .includes(destinationCountry.toLowerCase());

      const matchesFromDate = fromDate
        ? new Date(tx.createdAt) >= new Date(fromDate)
        : true;
      const matchesToDate = toDate
        ? new Date(tx.createdAt) <= new Date(toDate)
        : true;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesType &&
        matchesDestination &&
        matchesFromDate &&
        matchesToDate
      );
    });
  }, [
    transactions,
    search,
    statusFilter,
    typeFilter,
    fromDate,
    toDate,
    destinationCountry,
  ]);

  // ---------------------
  // Email modal
  // ---------------------
  const openMessages = (txId) => {
    setBulkEmailMode(false);
    setEmailModalTxId(String(txId));
    setShowEmailModal(true);
  };

  const openBulkEmail = () => {
    if (!selected.length) return;
    setBulkEmailMode(true);
    setShowEmailModal(true);
  };

  const viewDetails = (tx) => navigate(`/admin/transactions/${tx.id}`);

  // ---------------------
  // Cancel logic (single + bulk)
  // ---------------------
  const cancelEndpoint = (id) =>
    `${API_BASE}/api/admin/transactions/${id}/cancel`;

  const requestCancelOne = (txId) => setConfirmCancelId(String(txId));
  const cancelCancelOne = () => setConfirmCancelId(null);

  const cancelOne = async (txId) => {
    const id = String(txId);
    try {
      setCancelling(true);

      const res = await fetchWithAuth(cancelEndpoint(id), { method: "PATCH" });

      const data = await res.json().catch(() => ({}));
      if (!res.ok)
        throw new Error(data.message || "Impossible d’annuler la transaction");

      const updated = data.transaction || data.updatedTransaction || data;
      if (updated && updated.id != null) {
        setTransactions((prev) => dedupeById(upsertOne(prev, updated)));
      } else {
        handleStatusChange(id, { status: "cancelled" });
      }

      toast.success("Transaction annulée");
    } catch (e) {
      toast.error(e.message || "Erreur lors de l’annulation");
    } finally {
      setCancelling(false);
    }
  };

  const requestBulkCancel = () => {
    if (!selected.length) return;
    setConfirmBulkCancel(true);
  };
  const cancelBulkCancel = () => setConfirmBulkCancel(false);

  const bulkCancel = async () => {
    const ids = [...selected];
    if (!ids.length) return;

    let success = 0;
    let fail = 0;

    try {
      setCancelling(true);

      for (const id of ids) {
        try {
          const res = await fetchWithAuth(cancelEndpoint(id), {
            method: "PATCH",
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.message || "cancel failed");

          const updated = data.transaction || data.updatedTransaction || data;
          if (updated && updated.id != null) {
            setTransactions((prev) => dedupeById(upsertOne(prev, updated)));
          } else {
            handleStatusChange(id, { status: "cancelled" });
          }

          success++;
        } catch {
          fail++;
        }
      }

      toast.success(`Annulation terminée : ${success} ok, ${fail} échec(s)`);
      setSelected([]);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="p-3 sm:p-4 bg-white dark:bg-gray-800 rounded-lg shadow min-h-[calc(100vh-6rem)]">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
          Transactions
        </h2>

        {/* Quick actions */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={fetchTransactions}
            className="px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-900 text-sm
                       dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
          >
            Rafraîchir
          </button>

          {selected.length > 0 && (
            <>
              <button
                onClick={openBulkEmail}
                className="px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm"
              >
                Email ({selected.length})
              </button>

              <button
                onClick={requestBulkCancel}
                className="px-3 py-2 rounded-md bg-amber-700 hover:bg-amber-800 text-white text-sm"
              >
                Annuler ({selected.length})
              </button>
            </>
          )}
        </div>
      </div>

      <TransactionFilters
        search={search}
        setSearch={setSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
        destinationCountry={destinationCountry}
        setDestinationCountry={setDestinationCountry}
      />

      {loading ? (
        <p className="text-gray-600 dark:text-gray-300">
          Chargement des transactions...
        </p>
      ) : (
        <>
          <TransactionListCards
            transactions={transactions}
            selected={selected}
            toggleSelect={toggleSelect}
            onStatusChange={handleStatusChange}
            onViewDetails={viewDetails}
            onViewMessages={openMessages}
            onCancel={requestCancelOne}
            onNoteAdded={(transactionId) => {
              setTransactions((prev) =>
                prev.map((tx) =>
                  tx.id === transactionId
                    ? {
                        ...tx,
                        noteCount: (tx.noteCount || 0) + 1,
                      }
                    : tx,
                ),
              );
            }}
          />

          {selected.length > 0 && (
            <TransactionActionsBar
              selected={selected}
              onSendEmail={openBulkEmail}
              onSelectAll={() => toggleSelectAll(filtered)}
              allSelected={
                selected.length === filtered.length && filtered.length > 0
              }
              onCancelSelected={requestBulkCancel}
            />
          )}

          <div className="flex flex-wrap justify-center items-center gap-2 mt-6">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-2 bg-gray-200 rounded disabled:opacity-50 dark:bg-gray-700 dark:text-white"
            >
              ← Précédent
            </button>

            <span className="text-gray-700 dark:text-gray-300 text-sm">
              Page {page} / {totalPages}
            </span>

            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-2 bg-gray-200 rounded disabled:opacity-50 dark:bg-gray-700 dark:text-white"
            >
              Suivant →
            </button>
          </div>
        </>
      )}

      {showEmailModal && (
        <TransactionMessagesModal
          apiUrl={API_BASE}
          transactionId={bulkEmailMode ? selected : emailModalTxId}
          onClose={() => setShowEmailModal(false)}
          bulkMode={bulkEmailMode}
        />
      )}

      <ConfirmModal
        open={Boolean(confirmCancelId)}
        title="Annuler la transaction ?"
        description={
          confirmCancelId
            ? `Transaction ID : ${confirmCancelId}. Cette action annule la transaction (sans suppression).`
            : ""
        }
        confirmText="Annuler la transaction"
        cancelText="Retour"
        loading={cancelling}
        onCancel={cancelCancelOne}
        onConfirm={async () => {
          const id = confirmCancelId;
          setConfirmCancelId(null);
          await cancelOne(id);
        }}
      />

      <ConfirmModal
        open={confirmBulkCancel}
        title={`Annuler ${selected.length} transaction(s) ?`}
        description="Cette action annule les transactions sélectionnées (sans suppression)."
        confirmText="Annuler la sélection"
        cancelText="Retour"
        loading={cancelling}
        onCancel={cancelBulkCancel}
        onConfirm={async () => {
          setConfirmBulkCancel(false);
          await bulkCancel();
        }}
      />
    </div>
  );
}
